import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sg_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authApi = {
  register: (username, password, publicKey) =>
    api.post('/auth/register', { username, password, public_key: publicKey }),
  login: (username, password) =>
    api.post('/auth/login', { username, password }),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.patch('/users/me', data),
  updateSettings: (data) => api.patch('/users/me/settings', data),
  changePassword: (currentPassword, newPassword) =>
    api.post('/users/me/change-password', { current_password: currentPassword, new_password: newPassword }),
  deleteAccount: (password) =>
    api.delete('/users/me', { data: { password } }),
  searchUsers: (query) =>
    api.get('/users/search', { params: { q: query } }),
  getUserProfile: (userId) => api.get(`/users/${userId}/profile`),
};

// Messaging API
export const messagesApi = {
  send: (recipientId, content, lang = 'en', e2ePayload = null, media = null) =>
    api.post('/messages', { recipient_id: recipientId, content, lang, e2e_payload: e2ePayload, media }),
  getAll: () => api.get('/messages'),
  getConversations: () => api.get('/messages/conversations'),
  getConversation: (partnerId) => api.get(`/messages/${partnerId}`),
  deleteMessage: (messageId) => api.delete(`/messages/${messageId}`),
  deleteConversation: (partnerId) => api.delete(`/messages/conversation/${partnerId}`),
  getSecurityStatus: () => api.get('/messages/security/status'),
};

// Gems API
export const gemsApi = {
  getGems: (lang, page = 1, limit = 20, search = null, sort = 'newest') =>
    api.get('/gems', { params: { lang, page, limit, search, sort } }),
  createGem: (data) => api.post('/gems', data),
  toggleLike: (gemId, anonId) =>
    api.post(`/gems/${gemId}/like`, { anon_id: anonId }),
  reportGem: (gemId, anonId, reason) =>
    api.post(`/gems/${gemId}/report`, { anon_id: anonId, reason }),
  deleteGem: (gemId, anonId) =>
    api.delete(`/gems/${gemId}`, { params: { anon_id: anonId } }),
  translateGem: (gemId, targetLang) =>
    api.post(`/gems/${gemId}/translate`, { gem_id: gemId, target_lang: targetLang }),
};

// Comments API
export const commentsApi = {
  getComments: (gemId) => api.get(`/gems/${gemId}/comments`),
  createComment: (gemId, authorId, authorUsername, text, lang = 'en') =>
    api.post(`/gems/${gemId}/comments`, { gem_id: gemId, author_id: authorId, author_username: authorUsername, text, lang }),
  toggleLike: (commentId, ownerId) =>
    api.post(`/comments/${commentId}/like`, { owner_id: ownerId }),
  reportComment: (commentId, ownerId, reason) =>
    api.post(`/comments/${commentId}/report`, { owner_id: ownerId, reason }),
  deleteComment: (commentId, authorId, adminPassword = null) =>
    api.delete(`/comments/${commentId}`, { data: { author_id: authorId, admin_password: adminPassword } }),
  updateComment: (commentId, authorId, text) =>
    api.put(`/comments/${commentId}`, { author_id: authorId, text }),
  getReportedComments: (password) =>
    api.get('/admin/reported-comments', { params: { password } }),
};

// Bible API
export const bibleApi = {
  getEpubUrl: (lang) => api.get(`/bible/epub-url/${lang}`),
  getMp3Tracks: (lang) => api.get(`/bible/mp3-tracks/${lang}`),
  getEpubProxyUrl: (lang) => `${API_BASE}/api/bible/epub/${lang}`,
};

// Admin API
export const adminApi = {
  verifyPassword: (password) => api.post('/admin/verify', { password }),
  getReportedGems: (password) =>
    api.get('/admin/reported-gems', { params: { password } }),
  deleteGem: (gemId, password) =>
    api.delete(`/admin/gems/${gemId}`, { params: { password } }),
  getStats: (password) =>
    api.get('/admin/stats', { params: { password } }),
};

// Bookmarks API
export const bookmarksApi = {
  getBookmarks: (ownerId, query = null) =>
    api.get('/bookmarks', { params: { owner_id: ownerId, query } }),
  createBookmark: (data) => api.post('/bookmarks', data),
  deleteBookmark: (id, ownerId) =>
    api.delete(`/bookmarks/${id}`, { params: { owner_id: ownerId } }),
};

// Notes API
export const notesApi = {
  getNotes: (ownerId, query = null) =>
    api.get('/notes', { params: { owner_id: ownerId, query } }),
  createNote: (data) => api.post('/notes', data),
  updateNote: (id, data, ownerId) =>
    api.patch(`/notes/${id}`, data, { params: { owner_id: ownerId } }),
  deleteNote: (id, ownerId) =>
    api.delete(`/notes/${id}`, { params: { owner_id: ownerId } }),
};

// Languages API
export const languagesApi = {
  getLanguages: () => api.get('/languages'),
};

// Translation API
export const translationApi = {
  getStatus: () => api.get('/translation/status'),
  translateGem: (gemId, targetLang) =>
    api.post(`/gems/${gemId}/translate`, { gem_id: gemId, target_lang: targetLang }),
  translateComment: (commentId, targetLang) =>
    api.post(`/comments/${commentId}/translate`, { comment_id: commentId, target_lang: targetLang }),
  translateMessage: (messageId, targetLang) =>
    api.post(`/messages/${messageId}/translate`, { message_id: messageId, target_lang: targetLang }),
  translateText: (text, targetLang, sourceLang) =>
    api.post('/translate/text', { text, target_lang: targetLang, source_lang: sourceLang }),
};

// Preaching Reports API
export const preachingApi = {
  getReports: (category = null, page = 1, limit = 20, search = null) =>
    api.get('/preaching', { params: { category, page, limit, search } }),
  createReport: (data) => api.post('/preaching', data),
  toggleLike: (reportId, anonId) =>
    api.post(`/preaching/${reportId}/like`, { anon_id: anonId }),
  deleteReport: (reportId) => api.delete(`/preaching/${reportId}`),
};

// Notifications API
export const notificationsApi = {
  getNotifications: (page = 1, limit = 20) =>
    api.get('/notifications', { params: { page, limit } }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (notificationId) =>
    api.post(`/notifications/${notificationId}/read`),
  markAllRead: (type = null) =>
    api.post('/notifications/mark-all-read', type ? { type } : {}),
};

export default api;
